/* WPC datumplanner (gegenereerd door datumplanner/build.mjs; niet direct bewerken) */
(function () {
'use strict';
// ---- core.js ----
// WPC datumplanner: datumrekenwerk en beschikbaarheidsregels.
// Werkt uitsluitend met kalenderdatums als tekst 'JJJJ-MM-DD' in Europe/Amsterdam, zodat er nooit een dag verschuift door UTC.
// Dit bestand wordt ongewijzigd gebruikt door de tests, het ontwerp-prototype en het thema-asset (zie build.mjs).

const SCHEMA_VERSIE = 1;

const MAANDEN = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
const DAGEN = ['maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag', 'zondag'];
const DAGEN_KORT = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

// ---------- kalenderdatums ----------

function naarUtc(datum) {
  const [j, m, d] = datum.split('-').map(Number);
  return Date.UTC(j, m - 1, d);
}

function vanUtc(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

function plusDagen(datum, n) {
  return vanUtc(naarUtc(datum) + n * 86400000);
}

/** ISO-weekdag: 1 = maandag ... 7 = zondag */
function weekdag(datum) {
  const d = new Date(naarUtc(datum)).getUTCDay();
  return d === 0 ? 7 : d;
}

function verschilInDagen(van, tot) {
  return Math.round((naarUtc(tot) - naarUtc(van)) / 86400000);
}

/** Huidige datum en tijd in Europe/Amsterdam (of een andere tijdzone) voor een UTC-tijdstip in milliseconden. */
function lokaalNu(ms, tijdzone = 'Europe/Amsterdam') {
  const f = new Intl.DateTimeFormat('en-CA', { timeZone: tijdzone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const p = Object.fromEntries(f.formatToParts(new Date(ms)).map((x) => [x.type, x.value]));
  return { datum: `${p.year}-${p.month}-${p.day}`, tijd: `${p.hour}:${p.minute}` };
}

// ---------- ISO 8601-weken ----------

/** ISO-weekjaar en weeknummer van een datum. */
function isoWeek(datum) {
  const wd = weekdag(datum);
  const donderdag = plusDagen(datum, 4 - wd); // de donderdag van dezelfde week bepaalt het weekjaar
  const jaar = Number(donderdag.slice(0, 4));
  const week = Math.floor(verschilInDagen(`${jaar}-01-01`, donderdag) / 7) + 1;
  return { jaar, week };
}

/** Maandag van ISO-week 1 van een weekjaar. */
function maandagWeek1(jaar) {
  const vierJan = `${jaar}-01-04`;
  return plusDagen(vierJan, 1 - weekdag(vierJan));
}

function weekBereik(jaar, week) {
  const van = plusDagen(maandagWeek1(jaar), (week - 1) * 7);
  return { van, tot: plusDagen(van, 6) };
}

function wekenInJaar(jaar) {
  return isoWeek(`${jaar}-12-28`).week;
}

function maandagVan(datum) {
  return plusDagen(datum, 1 - weekdag(datum));
}

// ---------- Nederlandse tekst ----------

function datumLang(datum) {
  const [j, m, d] = datum.split('-').map(Number);
  return `${DAGEN[weekdag(datum) - 1]} ${d} ${MAANDEN[m - 1]} ${j}`;
}

function datumKort(datum) {
  const [j, m, d] = datum.split('-').map(Number);
  return `${d} ${MAANDEN[m - 1]} ${j}`;
}

/** "2–8 november 2026", "30 november – 6 december 2026", "28 december 2026 – 3 januari 2027" */
function bereikTekst(van, tot) {
  const [j1, m1, d1] = van.split('-').map(Number);
  const [j2, m2, d2] = tot.split('-').map(Number);
  if (j1 !== j2) return `${d1} ${MAANDEN[m1 - 1]} ${j1} – ${d2} ${MAANDEN[m2 - 1]} ${j2}`;
  if (m1 !== m2) return `${d1} ${MAANDEN[m1 - 1]} – ${d2} ${MAANDEN[m2 - 1]} ${j2}`;
  return `${d1}–${d2} ${MAANDEN[m2 - 1]} ${j2}`;
}

function tijdTekst(t) {
  return t.replace(':', '.');
}

function vensterTekst(vensters) {
  if (!vensters || !vensters.length) return '';
  const begin = vensters[0][0], eind = vensters[vensters.length - 1][1];
  const pauzes = [];
  for (let i = 1; i < vensters.length; i++) pauzes.push(`${tijdTekst(vensters[i - 1][1])}–${tijdTekst(vensters[i][0])}`);
  return `tussen ${tijdTekst(begin)} en ${tijdTekst(eind)} uur${pauzes.length ? ` (pauze ${pauzes.join(', ')})` : ''}`;
}

const maandNaam = (m) => MAANDEN[m - 1];
const dagKort = DAGEN_KORT;

// ---------- instellingen ----------

/** Standaardinstellingen. Waarden met `voorbeeld: true` zijn niet door WPC bevestigd en worden in de preview zo getoond. */
function standaardInstellingen() {
  return {
    versie: SCHEMA_VERSIE,
    tijdzone: 'Europe/Amsterdam',
    status: 'voorkeur', // 'voorkeur' of 'bevestigd'
    bezorgen: { bezorgdagen: [1, 2, 3, 4, 5], voorbereidingWerkdagen: 5, bestelgrens: '12:00', horizonWeken: 12, eersteWeken: 5, naAankomstWerkdagen: 2 },
    afhalen: {
      voorbereidingDagen: 1, bestelgrens: '12:00', horizonDagen: 60, naAankomstDagen: 1,
      locatie: { id: 'gid://shopify/Location/105308684633', naam: 'Showroom en afhaalpunt', adres: 'Manuscriptstraat 22, Almere' },
    },
    werkdagen: [1, 2, 3, 4, 5],
    // Openingstijden voor afhalen met ingangsdatum. Een latere regel vervangt per genoemde weekdag de eerdere.
    openingstijden: [
      { vanaf: '2000-01-01', dagen: { 1: [['10:00', '12:30'], ['13:00', '16:30']], 2: [['10:00', '12:30'], ['13:00', '16:30']], 3: [['10:00', '12:30'], ['13:00', '16:30']], 4: [['10:00', '12:30'], ['13:00', '16:30']], 5: [['10:00', '12:30'], ['13:00', '16:30']], 6: [], 7: [] } },
      { vanaf: '2026-11-01', dagen: { 6: [['10:00', '14:00']], 7: [['10:00', '14:00']] } },
    ],
    uitzonderingen: [], // { datum, type: 'gesloten'|'afwijkend', geldtVoor: ['afhalen','bezorgen'], tijden, reden }
    geblokkeerdeWeken: [], // { jaar, week, reden }
    voorbeeld: true,
  };
}

/** Openingstijden van de afhaallocatie op een datum, inclusief uitzonderingen. Lege lijst = gesloten. */
function openingstijdenOp(datum, inst) {
  const u = (inst.uitzonderingen || []).find((x) => x.datum === datum && (!x.geldtVoor || x.geldtVoor.includes('afhalen')));
  if (u) return u.type === 'gesloten' ? [] : (u.tijden || []);
  const wd = weekdag(datum);
  let tijden = [];
  for (const regel of [...(inst.openingstijden || [])].sort((a, b) => (a.vanaf < b.vanaf ? -1 : 1))) {
    if (regel.vanaf <= datum && Object.prototype.hasOwnProperty.call(regel.dagen, wd)) tijden = regel.dagen[wd];
  }
  return tijden;
}

function isGeslotenVoor(datum, soort, inst) {
  return (inst.uitzonderingen || []).some((x) => x.datum === datum && x.type === 'gesloten' && (!x.geldtVoor || x.geldtVoor.includes(soort)));
}

function isWerkdag(datum, inst) {
  return (inst.werkdagen || [1, 2, 3, 4, 5]).includes(weekdag(datum)) && !isGeslotenVoor(datum, 'bezorgen', inst);
}

function plusWerkdagen(datum, n, inst) {
  let d = datum, over = n;
  while (over > 0) { d = plusDagen(d, 1); if (isWerkdag(d, inst)) over--; }
  return d;
}

function plusOpenDagen(datum, n, inst) {
  let d = datum, over = n, veilig = 0;
  while (over > 0 && veilig++ < 400) { d = plusDagen(d, 1); if (openingstijdenOp(d, inst).length) over--; }
  return d;
}

// ---------- context van de bestelling ----------

/**
 * Bepaalt uit de artikelen het vroegste moment dat alles bij WPC is.
 * artikel: { titel, preorder: boolean, beschikbaarVanaf: 'JJJJ-MM-DD' | null }
 * Resultaat: { onzeker: [titels zonder datum], vanaf: laatste bekende beschikbaarheidsdatum of null }
 */
function beschikbaarheidArtikelen(artikelen) {
  const onzeker = [];
  let vanaf = null;
  for (const a of artikelen || []) {
    if (!a.preorder) continue;
    if (!a.beschikbaarVanaf) { onzeker.push(a.titel); continue; }
    if (!vanaf || a.beschikbaarVanaf > vanaf) vanaf = a.beschikbaarVanaf;
  }
  return { onzeker, vanaf };
}

/** Vroegste bezorgdatum op basis van nu, bestelgrens, voorbereiding en pre-orders. */
function vroegsteBezorgdatum(nu, inst, artikelen) {
  const b = inst.bezorgen;
  let start = nu.datum;
  if (!isWerkdag(start, inst) || nu.tijd >= b.bestelgrens) start = plusWerkdagen(start, 1, inst);
  let vroegst = plusWerkdagen(start, b.voorbereidingWerkdagen, inst);
  const art = beschikbaarheidArtikelen(artikelen);
  if (art.vanaf) {
    const naAankomst = plusWerkdagen(art.vanaf, b.naAankomstWerkdagen || 0, inst);
    if (naAankomst > vroegst) vroegst = naAankomst;
  }
  return vroegst;
}

/** Vroegste afhaaldag. Voorbereiding telt in dagen waarop de locatie open is. */
function vroegsteAfhaaldag(nu, inst, artikelen) {
  const a = inst.afhalen;
  let vroegst;
  if (a.voorbereidingDagen === 0 && nu.tijd < a.bestelgrens && openingstijdenOp(nu.datum, inst).length) vroegst = nu.datum;
  else {
    const extra = nu.tijd >= a.bestelgrens ? 1 : 0;
    vroegst = plusOpenDagen(nu.datum, Math.max(1, a.voorbereidingDagen) + extra, inst);
  }
  const art = beschikbaarheidArtikelen(artikelen);
  if (art.vanaf) {
    const naAankomst = plusOpenDagen(art.vanaf, a.naAankomstDagen || 0, inst);
    if (naAankomst > vroegst) vroegst = naAankomst;
  }
  return vroegst;
}

// ---------- aanbod ----------

/** Lijst leverweken met status. status: 'beschikbaar' | 'te-vroeg' | 'geblokkeerd' | 'geen-bezorgdagen' */
function leverweken(nu, inst, artikelen) {
  const b = inst.bezorgen;
  const vroegst = vroegsteBezorgdatum(nu, inst, artikelen);
  const eersteMaandag = maandagVan(nu.datum);
  const weken = [];
  for (let i = 0; i < b.horizonWeken; i++) {
    const van = plusDagen(eersteMaandag, i * 7), tot = plusDagen(van, 6);
    const { jaar, week } = isoWeek(van);
    const geblokkeerd = (inst.geblokkeerdeWeken || []).find((x) => x.jaar === jaar && x.week === week);
    const dagen = [];
    for (let k = 0; k < 7; k++) { const d = plusDagen(van, k); if (b.bezorgdagen.includes(weekdag(d)) && !isGeslotenVoor(d, 'bezorgen', inst)) dagen.push(d); }
    const haalbaar = dagen.filter((d) => d >= vroegst);
    let status = 'beschikbaar', reden = '';
    if (geblokkeerd) { status = 'geblokkeerd'; reden = geblokkeerd.reden || 'Geen bezorging in deze week'; }
    else if (!dagen.length) { status = 'geen-bezorgdagen'; reden = 'Geen bezorgdagen in deze week'; }
    else if (!haalbaar.length) { status = 'te-vroeg'; reden = 'Te vroeg voor deze bestelling'; }
    weken.push({ jaar, week, van, tot, status, reden, bezorgdagen: haalbaar });
  }
  return weken.filter((w) => w.tot >= nu.datum);
}

/** Dagen van een maand voor de afhaalkalender. status: 'beschikbaar' | 'gesloten' | 'te-vroeg' | 'voorbij' | 'buiten-horizon' */
function afhaaldagenMaand(jaar, maand, nu, inst, artikelen) {
  const vroegst = vroegsteAfhaaldag(nu, inst, artikelen);
  const laatst = plusDagen(nu.datum, inst.afhalen.horizonDagen);
  const eerste = `${jaar}-${String(maand).padStart(2, '0')}-01`;
  const dagen = [];
  for (let d = eerste; Number(d.slice(5, 7)) === maand; d = plusDagen(d, 1)) {
    const tijden = openingstijdenOp(d, inst);
    const uitz = (inst.uitzonderingen || []).find((x) => x.datum === d && (!x.geldtVoor || x.geldtVoor.includes('afhalen')));
    let status = 'beschikbaar', reden = '';
    if (d < nu.datum) { status = 'voorbij'; reden = 'Voorbij'; }
    else if (!tijden.length) { status = 'gesloten'; reden = uitz && uitz.reden ? `Gesloten: ${uitz.reden}` : 'Gesloten'; }
    else if (d > laatst) { status = 'buiten-horizon'; reden = 'Nog niet te plannen'; }
    else if (d < vroegst) { status = 'te-vroeg'; reden = 'Te vroeg voor deze bestelling'; }
    dagen.push({ datum: d, status, reden, tijden, vandaag: d === nu.datum });
  }
  return dagen;
}

// ---------- keuze en controle ----------

function handtekeningWinkelwagen(items) {
  return (items || []).map((i) => `${i.variant_id}x${i.quantity}`).sort().join(',');
}

/** Maakt het gegevensmodel dat op de bestelling komt. */
function maakKeuze(soort, waarde, inst, items, nuMs) {
  const basis = { v: SCHEMA_VERSIE, methode: soort, status: inst.status === 'bevestigd' ? 'bevestigd' : 'voorkeur', tz: inst.tijdzone, wagen: handtekeningWinkelwagen(items), op: new Date(nuMs).toISOString() };
  if (soort === 'bezorgen') {
    const { van, tot } = weekBereik(waarde.jaar, waarde.week);
    return { ...basis, weekjaar: waarde.jaar, week: waarde.week, van, tot };
  }
  if (soort === 'onbekend') return { ...basis, methode: waarde, status: 'onbekend' };
  const tijden = openingstijdenOp(waarde, inst);
  return { ...basis, datum: waarde, venster: tijden.length ? [tijden[0][0], tijden[tijden.length - 1][1]] : null, tijden, locatie: inst.afhalen.locatie };
}

/** Leesbare regels voor medewerkers en e-mail. */
function keuzeTekst(k) {
  if (!k) return '';
  const naam = k.methode === 'bezorgen' ? 'Bezorgen' : 'Afhalen';
  if (k.status === 'onbekend') return `${naam} — nog in te plannen: leverdatum van een pre-orderartikel is nog niet bekend`;
  const woord = k.status === 'bevestigd' ? '' : 'gewenste ';
  if (k.methode === 'bezorgen') return `Bezorgen — ${woord}leverweek ${k.week}, ${bereikTekst(k.van, k.tot)}`;
  return `Afhalen — ${woord ? 'gewenste afhaaldag ' : ''}${datumLang(k.datum)}${k.venster ? `, ${vensterTekst(k.tijden && k.tijden.length ? k.tijden : [k.venster])}` : ''}`;
}

/** Controleert of een opgeslagen keuze nog geldig is. Resultaat: { geldig, reden } */
function controleerKeuze(k, nu, inst, artikelen, methode) {
  if (!k) return { geldig: false, reden: 'geen-keuze' };
  if (k.v !== SCHEMA_VERSIE) return { geldig: false, reden: 'versie' };
  if (methode && k.methode !== methode) return { geldig: false, reden: 'andere-methode' };
  const art = beschikbaarheidArtikelen(artikelen);
  // Bij een pre-order zonder datum is "nog in te plannen" de enige eerlijke keuze, en ook alleen dan geldig.
  if (art.onzeker.length) return k.status === 'onbekend' ? { geldig: true } : { geldig: false, reden: 'onzeker' };
  if (k.status === 'onbekend') return { geldig: false, reden: 'datum-nu-bekend' };
  if (k.methode === 'bezorgen') {
    const w = leverweken(nu, inst, artikelen).find((x) => x.jaar === k.weekjaar && x.week === k.week);
    return w && w.status === 'beschikbaar' ? { geldig: true } : { geldig: false, reden: w ? w.status : 'verlopen' };
  }
  const [j, m] = k.datum.split('-').map(Number);
  const d = afhaaldagenMaand(j, m, nu, inst, artikelen).find((x) => x.datum === k.datum);
  return d && d.status === 'beschikbaar' ? { geldig: true } : { geldig: false, reden: d ? d.status : 'verlopen' };
}

// ---- ui.js ----
// WPC datumplanner: bediening (leverweek kiezen bij bezorgen, afhaaldag kiezen bij afhalen).
// Geen afhankelijkheden. Wordt in het ontwerp-prototype als module geladen en in het thema samen met core.js als één script.

const ICON = {
  truck: '<svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 6.5h11v9h-11z"/><path d="M13.5 9.5h4l3 3v3h-7"/><circle cx="6.5" cy="17" r="1.8"/><circle cx="17" cy="17" r="1.8"/></svg>',
  store: '<svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 10v10h16V10"/><path d="M3 10l2-6h14l2 6"/><path d="M3 10c0 1.7 1.3 3 3 3s3-1.3 3-3c0 1.7 1.3 3 3 3s3-1.3 3-3c0 1.7 1.3 3 3 3s3-1.3 3-3"/><path d="M10 20v-5h4v5"/></svg>',
  check: '<svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 8.5l3 3 6-7"/></svg>',
  left: '<svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7"/></svg>',
  right: '<svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5l7 7-7 7"/></svg>',
  clock: '<svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/></svg>',
  pin: '<svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0113 0c0 5.4-6.5 11-6.5 11z"/><circle cx="12" cy="10" r="2.3"/></svg>',
  cal: '<svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><rect x="3.5" y="5" width="17" height="15" rx="2.5"/><path d="M3.5 10h17M8 3v4M16 3v4"/></svg>',
  info: '<svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="12" cy="12" r="8.5"/><path d="M12 11v5M12 8h.01"/></svg>',
  alert: '<svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4l9 16H3z"/><path d="M12 10v4M12 17h.01"/></svg>',
};

const TEKST = {
  vraagMethode: 'Hoe wil je je bestelling ontvangen?',
  bezorgen: 'Bezorgen', afhalen: 'Afhalen',
  bezorgenSub: 'Door ons eigen team', afhalenSub: 'Manuscriptstraat 22, Almere',
  titelWeek: 'Kies je leverweek', instructieWeek: 'Selecteer de week waarin je je materialen wilt ontvangen.',
  titelDag: 'Kies je afhaaldag', instructieDag: 'Selecteer op welke dag je je bestelling wilt afhalen.',
  meerWeken: 'Meer weken bekijken', minderWeken: 'Minder weken tonen',
  kiesWeek: 'Kies een leverweek om verder te gaan.', kiesDag: 'Kies een afhaaldag om verder te gaan.', kiesMethode: 'Kies bezorgen of afhalen om verder te gaan.',
  dagVervallen: 'Deze afhaaldag is niet meer beschikbaar. Kies een andere dag.',
  weekVervallen: 'Deze leverweek is niet meer beschikbaar. Kies een andere week.',
  aangepast: 'Je bestelling is aangepast. Controleer je gekozen planning.',
  laadFout: 'We kunnen de beschikbare momenten nu niet laden. Probeer het opnieuw.',
  opslaanFout: 'Je keuze is nog niet opgeslagen. Probeer het opnieuw.',
  opnieuw: 'Opnieuw proberen', opslaan: 'Opslaan…', opgeslagen: 'Opgeslagen',
  geenWeken: 'Er zijn op dit moment geen leverweken beschikbaar voor deze bestelling.',
  geenDagen: 'Er zijn in deze maand geen afhaaldagen beschikbaar.',
  contact: 'Bel ons op 085 401 71 89 of mail naar info@wpc-solutions.com, dan plannen we samen.',
};

const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/**
 * opts: {
 *   instellingen, nuMs (servertijd), artikelen [{titel, preorder, beschikbaarVanaf}], items [{variant_id, quantity}],
 *   methode: 'bezorgen'|'afhalen'|null, keuze: opgeslagen keuze of null,
 *   opslaan: async (methode, keuze|null) => void   (gooit bij een fout),
 *   onWijziging: ({ methode, keuze, geldig }) => void,
 *   status: optioneel 'laden'|'laadfout' (voor het ontwerp), id: uniek voorvoegsel
 * }
 */
function maakPlanner(el, opts) {
  const inst = opts.instellingen;
  const id = opts.id || 'wpc-dp';
  const st = {
    methode: opts.methode || null, keuze: opts.keuze || null, opslag: opts.keuze ? 'opgeslagen' : 'leeg',
    melding: null, meerWeken: false, maand: null, focusDatum: null, laad: opts.status || 'klaar', volgnummer: 0,
  };
  const nu = () => lokaalNu(opts.nuMs ?? Date.now(), inst.tijdzone);
  const art = opts.artikelen || [];

  // Een eerder bewuste keuze komt alleen terug als die opnieuw geldig is.
  if (st.keuze) {
    const c = controleerKeuze(st.keuze, nu(), inst, art, st.methode);
    if (!c.geldig) {
      st.melding = { soort: 'fout', tekst: st.keuze.methode === 'afhalen' ? TEKST.dagVervallen : TEKST.weekVervallen };
      st.keuze = null; st.opslag = 'leeg';
      opts.opslaan && opts.opslaan(st.methode, null).catch(() => {});
    } else if (opts.items && st.keuze.wagen && st.keuze.wagen !== handtekening(opts.items)) {
      st.melding = { soort: 'info', tekst: TEKST.aangepast };
    }
  }

  function handtekening(items) { return items.map((i) => `${i.variant_id}x${i.quantity}`).sort().join(','); }

  function meld(tekst) {
    const live = el.querySelector(`#${id}-live`);
    if (live) { live.textContent = ''; setTimeout(() => { live.textContent = tekst; }, 30); }
  }

  function geldig() { return !!(st.keuze && st.opslag === 'opgeslagen' && st.keuze.methode === st.methode); }

  function meldWijziging() { opts.onWijziging && opts.onWijziging({ methode: st.methode, keuze: st.keuze, geldig: geldig(), opslag: st.opslag }); }

  async function bewaar(keuze) {
    const mijnNummer = ++st.volgnummer;
    st.keuze = keuze; st.opslag = 'bezig'; st.melding = null; teken(); meldWijziging();
    try {
      await opts.opslaan(st.methode, keuze);
      if (mijnNummer !== st.volgnummer) return; // een nieuwere keuze is onderweg; dit antwoord negeren
      st.opslag = 'opgeslagen'; teken(true); meldWijziging();
      meld(`${TEKST.opgeslagen}: ${keuzeTekst(keuze)}`);
    } catch (e) {
      if (mijnNummer !== st.volgnummer) return;
      st.opslag = 'fout'; teken(true); meldWijziging();
      meld(TEKST.opslaanFout);
    }
  }

  async function kiesMethode(m) {
    if (m === st.methode) return;
    st.methode = m; st.melding = null;
    if (st.keuze && st.keuze.methode !== m) { st.keuze = null; }
    st.opslag = 'leeg'; st.volgnummer++;
    if (isOnzeker()) { bewaar(maakKeuze('onbekend', m, inst, opts.items || [], opts.nuMs ?? Date.now())); return; }
    teken(); meldWijziging();
    try { await opts.opslaan(m, null); } catch (e) { /* de methode wordt bij de volgende keuze opnieuw meegestuurd */ }
  }

  function isOnzeker() { return beschikbaarheidArtikelen(art).onzeker.length > 0; }

  // ---------- tekenen ----------

  function methodeKeuze() {
    const optie = (m, icoon, titel, sub) => `
      <label class="wdp-m${st.methode === m ? ' is-on' : ''}">
        <input type="radio" form="${id}-los" name="${id}-methode" value="${m}"${st.methode === m ? ' checked' : ''}>
        <span class="wdp-m__ic">${icoon}</span>
        <span class="wdp-m__t"><b>${titel}</b><small>${sub}</small></span>
        <span class="wdp-m__dot" aria-hidden="true">${ICON.check}</span>
      </label>`;
    return `<fieldset class="wdp-ms"><legend class="wdp-lbl">${TEKST.vraagMethode}</legend>
      <div class="wdp-ms__row">${optie('bezorgen', ICON.truck, TEKST.bezorgen, TEKST.bezorgenSub)}${optie('afhalen', ICON.store, TEKST.afhalen, TEKST.afhalenSub)}</div></fieldset>`;
  }

  function melding() {
    if (!st.melding) return '';
    return `<div class="wdp-note wdp-note--${st.melding.soort}" role="${st.melding.soort === 'fout' ? 'alert' : 'status'}">${st.melding.soort === 'fout' ? ICON.alert : ICON.info}<span>${esc(st.melding.tekst)}</span></div>`;
  }

  function onzekerheid() {
    const o = beschikbaarheidArtikelen(art).onzeker;
    if (!o.length) return '';
    return `<div class="wdp-note wdp-note--info" role="status">${ICON.info}<span><b>Nog geen leverdatum bekend</b> voor ${o.map(esc).join(', ')}. We kunnen daarom nog geen ${st.methode === 'afhalen' ? 'afhaaldag' : 'leverweek'} aanbieden. Je kunt gewoon bestellen; we nemen contact op zodra de levering bekend is en plannen dan samen.</span></div>`;
  }

  function samenvatting() {
    if (!st.keuze) return `<p class="wdp-sum wdp-sum--leeg">${st.methode === 'afhalen' ? TEKST.kiesDag : TEKST.kiesWeek}</p>`;
    const k = st.keuze;
    const status = st.opslag === 'bezig' ? `<span class="wdp-save is-busy"><span class="wdp-spin" aria-hidden="true"></span>${TEKST.opslaan}</span>`
      : st.opslag === 'opgeslagen' ? `<span class="wdp-save is-ok">${ICON.check}${TEKST.opgeslagen}</span>`
      : st.opslag === 'fout' ? `<span class="wdp-save is-err">${ICON.alert}Niet opgeslagen</span>` : '';
    let regels;
    if (k.status === 'onbekend') {
      regels = `<p class="wdp-sum__k">Planning</p><p class="wdp-sum__v">Nog in te plannen</p>
        <p class="wdp-sum__s">${ICON.info}<span>We nemen contact met je op zodra de leverdatum bekend is.</span></p>`;
    } else if (k.methode === 'bezorgen') {
      regels = `<p class="wdp-sum__k">${k.status === 'bevestigd' ? 'Leverweek' : 'Gewenste leverweek'}</p>
        <p class="wdp-sum__v">Week ${k.week} · ${bereikTekst(k.van, k.tot)}</p>`;
    } else {
      regels = `<p class="wdp-sum__k">${k.status === 'bevestigd' ? 'Afhaaldag' : 'Gekozen afhaaldag'}</p>
        <p class="wdp-sum__v">${datumLang(k.datum).replace(/^./, (c) => c.toUpperCase())}</p>
        <p class="wdp-sum__s">${ICON.clock}<span>Afhalen ${k.venster ? vensterTekst(k.tijden && k.tijden.length ? k.tijden : [k.venster]) : ''}</span></p>
        <p class="wdp-sum__s">${ICON.pin}<span>${esc(inst.afhalen.locatie.adres)}</span></p>`;
    }
    const fout = st.opslag === 'fout' ? `<div class="wdp-note wdp-note--fout" role="alert">${ICON.alert}<span>${TEKST.opslaanFout}</span><button type="button" class="wdp-link" data-wdp="opnieuw">${TEKST.opnieuw}</button></div>` : '';
    return `<div class="wdp-sum${st.opslag === 'opgeslagen' ? ' is-ok' : ''}"><div class="wdp-sum__top">${status}</div>${regels}</div>${fout}`;
  }

  function bezorgUitleg() {
    const dagen = inst.bezorgen.bezorgdagen;
    const werk = dagen.length === 5 && [1, 2, 3, 4, 5].every((d) => dagen.includes(d));
    const tekst = werk ? 'werkdagen (maandag tot en met vrijdag)' : dagen.map((d) => dagKort[d - 1]).join(', ');
    return `We bezorgen op ${tekst}. De precieze dag stemmen we vooraf met je af.`;
  }

  function weekKiezer() {
    const alle = leverweken(nu(), inst, art);
    const eerste = alle.findIndex((w) => w.status === 'beschikbaar');
    if (eerste < 0) return `<div class="wdp-empty">${ICON.cal}<p>${TEKST.geenWeken}</p><p class="wdp-mut">${TEKST.contact}</p></div>`;
    const lijst = alle.slice(eerste);
    const zichtbaar = st.meerWeken ? lijst : lijst.slice(0, inst.bezorgen.eersteWeken);
    const kaarten = zichtbaar.map((w) => {
      const aan = st.keuze && st.keuze.methode === 'bezorgen' && st.keuze.weekjaar === w.jaar && st.keuze.week === w.week;
      const uit = w.status !== 'beschikbaar';
      const jaarTekst = w.jaar !== Number(w.van.slice(0, 4)) || w.jaar !== Number(w.tot.slice(0, 4)) ? ` (${w.jaar})` : '';
      const label = `Week ${w.week}${jaarTekst}, ${bereikTekst(w.van, w.tot)}${uit ? `, niet beschikbaar: ${w.reden}` : ''}`;
      return `<label class="wdp-w${aan ? ' is-on' : ''}${uit ? ' is-off' : ''}">
        <input type="radio" form="${id}-los" name="${id}-week" value="${w.jaar}-${w.week}"${aan ? ' checked' : ''}${uit ? ' disabled' : ''} aria-label="${esc(label)}">
        <span class="wdp-w__t"><b>Week ${w.week}${jaarTekst}</b><span>${bereikTekst(w.van, w.tot)}</span>${uit ? `<em class="wdp-w__r">${esc(w.reden)}</em>` : ''}</span>
        <span class="wdp-w__dot" aria-hidden="true">${ICON.check}</span>
      </label>`;
    }).join('');
    const meer = lijst.length > inst.bezorgen.eersteWeken ? `<button type="button" class="wdp-more" data-wdp="meer" aria-expanded="${st.meerWeken}">${st.meerWeken ? TEKST.minderWeken : TEKST.meerWeken}</button>` : '';
    return `<fieldset class="wdp-ws"><legend class="wdp-sr">${TEKST.titelWeek}</legend><div class="wdp-ws__grid">${kaarten}</div></fieldset>${meer}
      <p class="wdp-hint">${ICON.truck}<span>${esc(bezorgUitleg())}</span></p>`;
  }

  function dagKalender() {
    const n = nu();
    if (!st.maand) {
      const start = st.keuze && st.keuze.methode === 'afhalen' ? st.keuze.datum : n.datum;
      st.maand = { j: Number(start.slice(0, 4)), m: Number(start.slice(5, 7)) };
      // spring naar de eerste maand met een beschikbare dag
      for (let i = 0; i < 3 && !(st.keuze && st.keuze.methode === 'afhalen'); i++) {
        if (afhaaldagenMaand(st.maand.j, st.maand.m, n, inst, art).some((d) => d.status === 'beschikbaar')) break;
        st.maand = st.maand.m === 12 ? { j: st.maand.j + 1, m: 1 } : { j: st.maand.j, m: st.maand.m + 1 };
      }
    }
    const { j, m } = st.maand;
    const dagen = afhaaldagenMaand(j, m, n, inst, art);
    const kanTerug = `${j}-${String(m).padStart(2, '0')}` > n.datum.slice(0, 7);
    const horizon = afhaaldagenMaand(j, m, n, inst, art).some((d) => d.status === 'buiten-horizon');
    const eersteWd = (new Date(Date.UTC(j, m - 1, 1)).getUTCDay() + 6) % 7;
    const geselecteerd = st.keuze && st.keuze.methode === 'afhalen' ? st.keuze.datum : null;
    const eersteBeschikbaar = (dagen.find((d) => d.datum === geselecteerd) || dagen.find((d) => d.status === 'beschikbaar') || {}).datum;
    const focus = st.focusDatum && dagen.some((d) => d.datum === st.focusDatum) ? st.focusDatum : eersteBeschikbaar;
    let cellen = '';
    for (let i = 0; i < eersteWd; i++) cellen += '<span class="wdp-d wdp-d--pad" role="presentation"></span>';
    for (const d of dagen) {
      const aan = d.datum === geselecteerd;
      const ok = d.status === 'beschikbaar';
      const label = `${datumLang(d.datum)}${d.vandaag ? ', vandaag' : ''}${ok ? (d.tijden.length ? `, open ${vensterTekst(d.tijden)}` : '') : `, niet beschikbaar: ${d.reden}`}${aan ? ', gekozen' : ''}`;
      cellen += `<button type="button" class="wdp-d${ok ? ' is-ok' : ' is-off'}${aan ? ' is-on' : ''}${d.vandaag ? ' is-today' : ''}" data-dag="${d.datum}" aria-label="${esc(label)}" aria-pressed="${aan}"${ok ? '' : ' aria-disabled="true"'} tabindex="${d.datum === focus ? 0 : -1}"><span>${Number(d.datum.slice(8))}</span></button>`;
    }
    const geen = dagen.every((d) => d.status !== 'beschikbaar') ? `<p class="wdp-mut wdp-cal__geen">${TEKST.geenDagen}</p>` : '';
    return `<div class="wdp-cal" role="group" aria-labelledby="${id}-maand">
      <div class="wdp-cal__head">
        <button type="button" class="wdp-nav" data-wdp="vorige" aria-label="Vorige maand"${kanTerug ? '' : ' disabled'}>${ICON.left}</button>
        <p class="wdp-cal__maand" id="${id}-maand" aria-live="polite">${maandNaam(m).replace(/^./, (c) => c.toUpperCase())} ${j}</p>
        <button type="button" class="wdp-nav" data-wdp="volgende" aria-label="Volgende maand"${horizon && !dagen.some((d) => d.status === 'beschikbaar' && d.datum > n.datum) ? ' disabled' : ''}>${ICON.right}</button>
      </div>
      <div class="wdp-cal__wd" aria-hidden="true">${dagKort.map((x) => `<span>${x}</span>`).join('')}</div>
      <div class="wdp-cal__grid">${cellen}</div>${geen}
      <ul class="wdp-legend" aria-label="Legenda">
        <li><span class="wdp-lg wdp-lg--ok"></span>Beschikbaar</li><li><span class="wdp-lg wdp-lg--on"></span>Gekozen</li><li><span class="wdp-lg wdp-lg--off"></span>Gesloten of niet mogelijk</li><li><span class="wdp-lg wdp-lg--today"></span>Vandaag</li>
      </ul></div>`;
  }

  function teken(alleenSamenvatting) {
    if (alleenSamenvatting && el.querySelector('.wdp-sumwrap')) { el.querySelector('.wdp-sumwrap').innerHTML = samenvatting(); return; }
    const onzeker = beschikbaarheidArtikelen(art).onzeker.length > 0;
    let inhoud = '';
    if (st.laad === 'laden') inhoud = `<div class="wdp-skel" aria-hidden="true"><span></span><span></span><span></span></div><p class="wdp-sr" role="status">Beschikbare momenten worden geladen</p>`;
    else if (st.laad === 'laadfout') inhoud = `<div class="wdp-note wdp-note--fout" role="alert">${ICON.alert}<span>${TEKST.laadFout} ${TEKST.contact}</span><button type="button" class="wdp-link" data-wdp="herlaad">${TEKST.opnieuw}</button></div>`;
    else if (st.methode && onzeker) inhoud = `<div class="wdp-sumwrap" aria-live="polite">${st.keuze ? samenvatting() : ''}</div>`;
    else if (st.methode) {
      const titel = st.methode === 'afhalen' ? TEKST.titelDag : TEKST.titelWeek;
      const instructie = st.methode === 'afhalen' ? TEKST.instructieDag : TEKST.instructieWeek;
      inhoud = `<div class="wdp-stap"><h3 class="wdp-sub" id="${id}-sub">${titel}</h3><p class="wdp-intro">${instructie}</p></div>`
        + (st.methode === 'bezorgen'
          ? weekKiezer() + `<div class="wdp-sumwrap" aria-live="polite">${samenvatting()}</div>`
          : `<div class="wdp-pick">${dagKalender()}<div class="wdp-sumwrap" aria-live="polite">${samenvatting()}</div></div>`);
    }
    el.innerHTML = `<section class="wdp" aria-labelledby="${id}-titel">
      <div class="wdp-head"><span class="wdp-head__ic">${ICON.cal}</span><div><h2 class="wdp-title" id="${id}-titel">Levering plannen</h2><p class="wdp-intro">${st.methode ? 'Kies hieronder het moment dat jou uitkomt.' : 'Kies hoe je je bestelling wilt ontvangen. Daarna kies je het moment.'}</p></div></div>
      ${methodeKeuze()}${melding()}${st.methode ? onzekerheid() : ''}${inhoud}
      ${inst.voorbeeld ? '<p class="wdp-demo">Voorbeeldinstellingen: beschikbaarheid nog niet door WPC bevestigd.</p>' : ''}
      <p class="wdp-sr" id="${id}-live" aria-live="polite"></p></section>`;
  }

  // ---------- bediening ----------

  const stop = new AbortController();
  const luister = { signal: stop.signal };
  el.addEventListener('change', (e) => {
    const t = e.target;
    if (t.name === `${id}-methode`) { kiesMethode(t.value); return; }
    if (t.name === `${id}-week`) {
      const [j, w] = t.value.split('-').map(Number);
      bewaar(maakKeuze('bezorgen', { jaar: j, week: w }, inst, opts.items || [], opts.nuMs ?? Date.now()));
      requestAnimationFrame(() => { teken(); const r = el.querySelector(`input[name="${id}-week"][value="${t.value}"]`); r && r.focus(); });
    }
  }, luister);

  el.addEventListener('click', (e) => {
    const knop = e.target.closest('[data-wdp],[data-dag]');
    if (!knop || !el.contains(knop)) return;
    const actie = knop.getAttribute('data-wdp');
    if (actie === 'meer') { st.meerWeken = !st.meerWeken; teken(); el.querySelector('[data-wdp="meer"]').focus(); return; }
    if (actie === 'vorige' || actie === 'volgende') {
      const { j, m } = st.maand;
      st.maand = actie === 'vorige' ? (m === 1 ? { j: j - 1, m: 12 } : { j, m: m - 1 }) : (m === 12 ? { j: j + 1, m: 1 } : { j, m: m + 1 });
      st.focusDatum = null; teken(); const k = el.querySelector(`[data-wdp="${actie}"]`); (k && !k.disabled ? k : el.querySelector('.wdp-d[tabindex="0"]'))?.focus(); return;
    }
    if (actie === 'opnieuw' && st.keuze) { bewaar(st.keuze); return; }
    if (actie === 'herlaad') { opts.herlaad ? opts.herlaad() : location.reload(); return; }
    const dag = knop.getAttribute('data-dag');
    if (dag) {
      if (knop.getAttribute('aria-disabled') === 'true') { meld(knop.getAttribute('aria-label')); return; }
      st.focusDatum = dag;
      bewaar(maakKeuze('afhalen', dag, inst, opts.items || [], opts.nuMs ?? Date.now()));
      requestAnimationFrame(() => { teken(); el.querySelector(`[data-dag="${dag}"]`)?.focus(); });
    }
  }, luister);

  // Pijltjestoetsen in de kalender (roving tabindex), Home/End per week, PageUp/PageDown per maand.
  el.addEventListener('keydown', (e) => {
    const d = e.target.closest && e.target.closest('.wdp-d[data-dag]');
    if (!d) return;
    const stap = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];
    const cur = d.getAttribute('data-dag');
    let doel = null;
    if (stap) doel = verschuif(cur, stap);
    else if (e.key === 'Home') doel = verschuif(cur, -((new Date(cur + 'T12:00:00Z').getUTCDay() + 6) % 7));
    else if (e.key === 'End') doel = verschuif(cur, 6 - ((new Date(cur + 'T12:00:00Z').getUTCDay() + 6) % 7));
    else if (e.key === 'PageUp' || e.key === 'PageDown') { e.preventDefault(); el.querySelector(`[data-wdp="${e.key === 'PageUp' ? 'vorige' : 'volgende'}"]`)?.click(); return; }
    if (!doel) return;
    e.preventDefault();
    const zelfdeMaand = doel.slice(0, 7) === cur.slice(0, 7);
    st.focusDatum = doel;
    if (!zelfdeMaand) { st.maand = { j: Number(doel.slice(0, 4)), m: Number(doel.slice(5, 7)) }; teken(); }
    else { el.querySelectorAll('.wdp-d[data-dag]').forEach((b) => b.setAttribute('tabindex', b.getAttribute('data-dag') === doel ? '0' : '-1')); }
    el.querySelector(`[data-dag="${doel}"]`)?.focus();
  }, luister);

  function verschuif(datum, n) {
    const t = new Date(datum + 'T12:00:00Z'); t.setUTCDate(t.getUTCDate() + n);
    return t.toISOString().slice(0, 10);
  }

  teken();
  meldWijziging();
  if (st.methode && isOnzeker() && !(st.keuze && st.keuze.status === 'onbekend') && opts.opslaan && st.laad === 'klaar') {
    bewaar(maakKeuze('onbekend', st.methode, inst, opts.items || [], opts.nuMs ?? Date.now()));
  }
  // Winkelwagen gewijzigd maar keuze nog geldig: opnieuw opslaan met de nieuwe inhoud, zodat de controle bij afrekenen klopt.
  if (st.keuze && st.opslag === 'opgeslagen' && opts.items && st.keuze.wagen !== handtekening(opts.items) && opts.opslaan) {
    bewaar({ ...st.keuze, wagen: handtekening(opts.items) });
  }
  return {
    opruimen: () => { stop.abort(); st.volgnummer++; },
    status: () => ({ methode: st.methode, keuze: st.keuze, geldig: geldig(), opslag: st.opslag }),
    zetLaden: (s) => { st.laad = s; teken(); },
    toonMelding: (soort, tekst) => { st.melding = { soort, tekst }; teken(); },
    openingstijden: (datum) => openingstijdenOp(datum, inst),
  };
}

// ---- thema.js ----
// WPC datumplanner: koppeling met het Shopify-thema.
// - Leest instellingen (metaobjecten, via Liquid als JSON in de pagina) en de artikelgegevens.
// - Gebruikt de Date-header van /cart.js als tijdsbron, niet de klok van de klant.
// - Bewaart de keuze als winkelwagenattributen; Shopify zet die op de bestelling (Aanvullende gegevens).
// - Laat afrekenen pas door na een geldige, opgeslagen keuze, en stuurt de mini-winkelwagen naar /cart als dat nog niet zo is.


const ATTR = { methode: 'Leveringsmethode', planning: 'Planning', data: '_planning' };
const NAAR_PLANNER = '/cart#wpc-planning';

function leesJson(id) {
  const el = document.getElementById(id);
  if (!el) return null;
  try { return JSON.parse(el.textContent); } catch (e) { return null; }
}

function instellingen() {
  const uitPagina = leesJson('wpc-dp-instellingen');
  return uitPagina && uitPagina.versie ? uitPagina : standaardInstellingen();
}

async function haalWagen() {
  const r = await fetch('/cart.js', { cache: 'no-store', headers: { accept: 'application/json' } });
  if (!r.ok) throw new Error('cart ' + r.status);
  const wagen = await r.json();
  const datum = Date.parse(r.headers.get('date') || '');
  return { wagen, nuMs: Number.isFinite(datum) ? datum : Date.now() };
}

function keuzeUitWagen(wagen) {
  const a = (wagen && wagen.attributes) || {};
  let keuze = null;
  try { keuze = a[ATTR.data] ? JSON.parse(a[ATTR.data]) : null; } catch (e) { keuze = null; }
  const m = (a[ATTR.methode] || '').toLowerCase();
  const methode = m.startsWith('bezorg') ? 'bezorgen' : m.startsWith('afhal') ? 'afhalen' : (keuze && keuze.methode) || null;
  return { keuze, methode };
}

async function bewaarAttributen(methode, keuze) {
  const attributes = {
    [ATTR.methode]: methode === 'bezorgen' ? 'Bezorgen' : methode === 'afhalen' ? 'Afhalen' : '',
    [ATTR.planning]: keuze ? keuzeTekst(keuze) : '',
    [ATTR.data]: keuze ? JSON.stringify(keuze) : '',
  };
  const r = await fetch('/cart/update.js', { method: 'POST', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify({ attributes }) });
  if (!r.ok) throw new Error('opslaan ' + r.status);
  const wagen = await r.json();
  // Pas "opgeslagen" melden als Shopify de waarde echt terugstuurt.
  const terug = (wagen.attributes || {})[ATTR.data] || '';
  if (terug !== attributes[ATTR.data]) throw new Error('opslaan niet bevestigd');
  return wagen;
}

function artikelen() {
  return leesJson('wpc-dp-artikelen') || [];
}

/** Is de opgeslagen planning nu geldig voor deze winkelwagen? Gebruikt buiten de winkelwagenpagina (mini-winkelwagen). */
async function planningGeldig() {
  const { wagen, nuMs } = await haalWagen();
  const { keuze, methode } = keuzeUitWagen(wagen);
  if (!keuze) return false;
  const handtekening = wagen.items.map((i) => `${i.variant_id}x${i.quantity}`).sort().join(',');
  if (keuze.wagen !== handtekening) return false; // winkelwagen gewijzigd sinds de keuze: eerst opnieuw laten controleren
  const inst = instellingen();
  const art = keuze.status === 'onbekend' ? [{ titel: '', preorder: true, beschikbaarVanaf: null }] : [];
  return controleerKeuze(keuze, lokaalNu(nuMs, inst.tijdzone), inst, art, methode).geldig;
}

// ---------- winkelwagenpagina ----------

function startPlanner() {
  const houder = document.getElementById('wpc-datumplanner');
  if (!houder) return;
  const inst = instellingen();
  let planner = null, status = { geldig: false, methode: null };
  const rec = document.getElementById('wpc-dp-samenvatting');

  function toonSamenvatting(s) {
    status = s;
    document.documentElement.toggleAttribute('data-wpc-planning-ok', !!s.geldig);
    if (!rec) return;
    rec.hidden = false;
    rec.classList.toggle('is-ok', !!s.geldig);
    rec.textContent = s.geldig ? keuzeTekst(s.keuze)
      : s.methode === 'afhalen' ? 'Kies een afhaaldag om verder te gaan.'
      : s.methode === 'bezorgen' ? 'Kies een leverweek om verder te gaan.'
      : 'Kies bezorgen of afhalen om verder te gaan.';
  }

  async function laad(melding) {
    if (!planner) { houder.innerHTML = ''; planner = maakPlanner(houder, { instellingen: inst, nuMs: Date.now(), status: 'laden', opslaan: async () => {}, id: 'wpc-dp' }); }
    try {
      const { wagen, nuMs } = await haalWagen();
      const { keuze, methode } = keuzeUitWagen(wagen);
      planner && planner.opruimen && planner.opruimen();
      houder.innerHTML = '';
      planner = maakPlanner(houder, {
        instellingen: inst, nuMs, items: wagen.items.map((i) => ({ variant_id: i.variant_id, quantity: i.quantity })),
        artikelen: artikelen(), methode, keuze, id: 'wpc-dp',
        opslaan: (m, k) => bewaarAttributen(m, k),
        onWijziging: toonSamenvatting,
        herlaad: () => laad(),
      });
      if (melding) planner.toonMelding('info', melding);
    } catch (e) {
      planner && planner.opruimen && planner.opruimen();
      houder.innerHTML = '';
      planner = maakPlanner(houder, { instellingen: inst, nuMs: Date.now(), status: 'laadfout', opslaan: async () => {}, onWijziging: toonSamenvatting, herlaad: () => laad(), id: 'wpc-dp' });
    }
  }

  // Afrekenen op de winkelwagenpagina: alleen met een geldige, opgeslagen keuze.
  document.addEventListener('submit', (e) => {
    const f = e.target;
    if (!(f instanceof HTMLFormElement) || !/\/cart\/?$/.test(new URL(f.action, location.href).pathname)) return;
    const knop = e.submitter;
    if (!knop || knop.name !== 'checkout') return;
    if (status.geldig) return;
    e.preventDefault();
    vraagOmKeuze();
  }, true);

  function vraagOmKeuze() {
    const tekst = status.methode === 'afhalen' ? 'Kies een afhaaldag om verder te gaan.' : status.methode === 'bezorgen' ? 'Kies een leverweek om verder te gaan.' : 'Kies bezorgen of afhalen om verder te gaan.';
    planner && planner.toonMelding('fout', tekst);
    houder.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    const doel = houder.querySelector('input[name$="-week"]:not(:disabled), .wdp-d[tabindex="0"]') || houder.querySelector('input[name$="-methode"]');
    doel && doel.focus({ preventScroll: true });
  }

  // Wijzigingen in de winkelwagen (aantal, verwijderen) via het thema: opnieuw controleren.
  const origFetch = window.fetch;
  let herTimer = null;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const p = origFetch.apply(this, arguments);
    if (/\/cart\/(add|change|clear)(\.js)?/.test(url)) {
      p.then(() => { clearTimeout(herTimer); herTimer = setTimeout(() => laad('Je bestelling is aangepast. Controleer je gekozen planning.'), 300); }).catch(() => {});
    }
    return p;
  };

  laad();
  window.WpcDatumplanner = { status: () => status, vraagOmKeuze, herlaad: () => laad() };
}

// ---------- overal: routes naar de checkout ----------

function bewaakCheckoutLinks() {
  document.addEventListener('click', async (e) => {
    const a = e.target.closest && e.target.closest('a[href]');
    if (!a) return;
    const url = new URL(a.getAttribute('href'), location.href);
    if (url.origin !== location.origin || !/^\/checkout(s)?(\/|$)/.test(url.pathname)) return;
    e.preventDefault();
    if (document.getElementById('wpc-datumplanner') && window.WpcDatumplanner) {
      if (window.WpcDatumplanner.status().geldig) location.href = a.href; else window.WpcDatumplanner.vraagOmKeuze();
      return;
    }
    let ok = false;
    try { ok = await planningGeldig(); } catch (err) { ok = false; }
    location.href = ok ? a.href : NAAR_PLANNER;
  }, true);
}

function start() {
  bewaakCheckoutLinks();
  startPlanner();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();

})();
